// The two WebAuthn ceremonies, each a challenge from the server, a browser prompt, and the answer
// posted back. @simplewebauthn/browser does the base64url work and the options conversion.
import { browserSupportsWebAuthn, startAuthentication, startRegistration, WebAuthnError } from '@simplewebauthn/browser'
import type { PublicKeyCredentialCreationOptionsJSON, PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser'
import { auth, type SignInResponse, type UserInfo } from './api'

export function passkeysSupported(): boolean {
  return browserSupportsWebAuthn()
}

export async function signInWithPasskey(): Promise<SignInResponse> {
  const { stateId, options } = await auth.passkeyLoginOptions()
  const response = await startAuthentication({ optionsJSON: options as PublicKeyCredentialRequestOptionsJSON })
  return auth.passkeyLogin(stateId, response)
}

export async function addPasskey(name: string): Promise<UserInfo> {
  const { stateId, options } = await auth.passkeyRegisterOptions()
  const response = await startRegistration({ optionsJSON: options as PublicKeyCredentialCreationOptionsJSON })
  return auth.passkeyRegister(stateId, response, name)
}

/** What to tell the user when the browser, rather than the server, said no. */
export function passkeyErrorMessage(error: unknown): string | null {
  if (!(error instanceof WebAuthnError) && !(error instanceof DOMException)) return null
  switch (error.name) {
    case 'NotAllowedError':
      return 'The passkey prompt was cancelled or timed out.'
    case 'InvalidStateError':
      return 'This device already holds a passkey for your account.'
    case 'SecurityError':
      return 'Passkeys are not available on this address. Use the site’s real host name over https.'
    default:
      return `The browser could not complete the passkey step (${error.name}).`
  }
}
